import React, { useMemo } from 'react'
import Table from '../../../components/common/Table/Table'
import { getRequestDetailsTypeLabel } from '../../../utils/timeOffTypes'
import { formatDateToMMDDYYYY, formatTimesDisplay } from '../../../utils/date'
import { formatHours } from '../../../utils/formatters'
import { ApproverRequestDetails, RequestedDate } from '../../../store/slices/Approver/approverRequestDetailsSlice'
import { BalanceInfo, UpcomingApprovedHours } from '../../../types/timeOffRequestDetails'
import RequestHistory from './RequestHistory'

/**
 * Adds thousands separators to a balance/hours cell value once it is fully
 * computed. Non-numeric placeholders ('-', 'NA', 'Included above') pass
 * through unchanged — this only reformats genuine numeric strings, so it is
 * safe to run after every value that feeds into a sum (Number(cellValue))
 * has already been read.
 */
const formatBalanceCell = (value: string): string => {
  const numeric = Number(value)
  return Number.isNaN(numeric) ? value : formatHours(numeric)
}

interface TableRow {
  [key: string]: any
  _totalRow?: boolean
  _rowStyle?: React.CSSProperties
}

interface DetailsTableProps {
  requestedDates: RequestedDate[] | undefined
  balance: BalanceInfo | undefined
  upcomingApprovedHours: UpcomingApprovedHours | undefined
  data: ApproverRequestDetails
   | null
  approvedHoursById?: Record<string, number>
}

/**
 * Formats time strings for display.
 * Handles "Full Day", ISO timestamps, and formatted time strings.
 *
 * Exported so any other table showing the same requestedDates.times field
 * (e.g. the Partial Approve/Deny date table) renders it identically to the
 * Current Request table instead of falling back to a raw ISO string.
 */

/**
 * DetailsTable component — displays both Current Request and Request Details tables.
 * Consolidates table logic for cleaner component hierarchy.
 */
const DetailsTable: React.FC<DetailsTableProps> = ({
  requestedDates,
  balance,
  upcomingApprovedHours,
  data,
  approvedHoursById,
}) => {
  /**
   * Build current request table rows from requestedDates.
   */
  const currentRequestRows = useMemo(() => {
    const rows: TableRow[] = []

    let approvedTotal = 0
    let requestedTotal = 0

    // Once a request lands in one of these terminal/rejected statuses,
    // nothing was (or remains) approved — force the Approved Days/Hours
    // column to 0 regardless of any stale approvedHoursById mapping.
    const requestStatus = data?.statuses?.[0]?.status?.toUpperCase()
    const isZeroApprovedStatus = ['DENIED', 'PDENIED', 'RETRACTED', 'PRETRACTED'].includes(
      requestStatus ?? ''
    )

    ;(requestedDates ?? []).forEach((date, idx) => {
      const idKey = date.toReqDtlId ? String(date.toReqDtlId) : `date-${idx}`
      const requested = Number(date.requestedHours || 0)
      const approved = isZeroApprovedStatus
        ? 0
        : Number(approvedHoursById?.[idKey] ?? requested)

      requestedTotal += requested
      approvedTotal += approved

      rows.push({
        id: idKey,
        requestType: getRequestDetailsTypeLabel(date.requestType, date.toSubTypeCd),
        date: formatDateToMMDDYYYY(date.date),
        day: date.day,
        times: formatTimesDisplay(date.times),
        requestedHrs: String(requested.toFixed(2)),
        approvedDaysHours: String(approved.toFixed(2)),
      })
    })

    // Single total row: display both Requested Hours and Approved Hours
    rows.push({
      id: 'total',
      requestType: 'Total Hours',
      date: '',
      day: '',
      times: '',
      requestedHrs: formatHours(requestedTotal),
      approvedDaysHours: formatHours(approvedTotal),
      _totalRow: true,
      _rowStyle: {
        backgroundColor: '#D3D6DC',
        border: '1px solid #878F9B',
      },
    })

    return rows
  }, [requestedDates, approvedHoursById, data])

  const topStatus = data?.statuses?.[0]?.status?.toUpperCase()
  const isPartialRetractionStatus = ['PRTSUBMITTED', 'PRTSUBMITTED_RELEASED', 'PRTSUBMITTED_ONHOLD'].includes(topStatus ?? '')

  const currentRequestColumns = [
    { key: 'requestType', label: 'Request Type', className: '' },
    { key: 'date', label: 'Date', className: '' },
    { key: 'day', label: 'Day', className: '' },
    { key: 'times', label: 'Times', className: '' },
    { key: 'requestedHrs', label: 'Requested hours', className: '' },
    { key: 'approvedDaysHours', label: isPartialRetractionStatus ? 'Days Retracted' : 'Approved Days/Hours', className: '' },
  ]

  /**
   * Build request details table rows for each mandatory request type.
   * Always displays all types with "-" for hours if not in the request.
   * Groups "Other" types by subtype code.
   */
const requestDetailsRows = useMemo(() => {
  const rows: TableRow[] = []

  const typeHoursMap = new Map<string, number>()
  const otherSubtypeHoursMap = new Map<string, number>()

  const requestStatus =
    data?.statuses?.[0]?.status?.toUpperCase()

  const isDenied = requestStatus === 'DENIED'

  const hasNonOverlapped =
    (requestedDates ?? []).some(
      (date) => date.isOverlapped === false
    )


  /*
   * Calculate allocation hours
   */
  ;(requestedDates ?? []).forEach((date) => {

    ;(date.timeOffAllocations ?? []).forEach((allocation) => {

      const hours = Number(allocation.hours || 0)
      const type = allocation.toTypeCd


      // Always calculate Other
      if (type === 'O') {

        if (allocation.toSubTypeCd) {

          otherSubtypeHoursMap.set(
            allocation.toSubTypeCd,
            (otherSubtypeHoursMap.get(allocation.toSubTypeCd) || 0)
            + hours
          )
        }
        return
      }
      // Calculate V,A,F,D only if not overlapped
      if (hasNonOverlapped) {
        typeHoursMap.set(
          type,
          (typeHoursMap.get(type) || 0) + hours
        )
      }
    })
  })
  const getHours = (type:string) => {
    if (isDenied) {
      return '0.00'
    }
    return (
      typeHoursMap.get(type) || 0
    ).toFixed(2)
  }
  const getOtherHours = (type:string) => {

    if (isDenied) {
      return '0.00'
    }

    return (
      otherSubtypeHoursMap.get(type) || 0
    ).toFixed(2)

  }



  /*
   * Vacation
   */
  if (balance?.vacation?.available !== 9999.99) {

    rows.push({

      timeOffType:
        <>
          {getRequestDetailsTypeLabel('V')}
          <span style={{color:'red'}}>*</span>
        </>,

      currentBalance:
        Number(balance?.vacation?.current ?? 0)
        .toFixed(2),

      availableBalance:
        Number(balance?.vacation?.available ?? 0)
        .toFixed(2),

      futureHoursApproved:
        Number(
          upcomingApprovedHours?.futApprovedVacHours ?? 0
        ).toFixed(2),

      hoursInThisRequest:
        getHours('V')

    })

  }
  /*
   * Vacation Bidding
   */
  rows.push({

    timeOffType:
      getRequestDetailsTypeLabel('A'),

    currentBalance:'-',

    availableBalance:
      'Included above',

    futureHoursApproved:
      Number(
        upcomingApprovedHours?.futApprovedVacBidHours ?? 0
      ).toFixed(2),

    hoursInThisRequest:
      getHours('A')

  })
  /*
   * Float
   */
  if (balance?.float?.available !== 9999.99) {
    rows.push({
      timeOffType:
        getRequestDetailsTypeLabel('F'),
      currentBalance:
        Number(balance?.float?.current ?? 0)
        .toFixed(2),
      availableBalance:
        balance?.float?.available === 8888.88
          ? 'NA'
          :
          Number(balance?.float?.available ?? 0)
          .toFixed(2),
      futureHoursApproved:
        Number(
          upcomingApprovedHours?.futApprovedFloatHours ?? 0
        ).toFixed(2),
      hoursInThisRequest:
        getHours('F')
    })
  }
  /*
   * Other Types
   */
  const otherTypes = [
    'OTH_JD',
    'OTH_UB',
    'OTH_BL',
    'OTH_TM',
    'OTH_ET',
    'OTH_DA',
    'OTH_UT',
    'OTH_LT',
    'OTH_PT'
  ]
  otherTypes.forEach((subType)=>{
    rows.push({
      timeOffType:
        getRequestDetailsTypeLabel(
          'O',
          subType
        ),
      currentBalance:'-',
      availableBalance:'-',
      futureHoursApproved:
        Number(
          upcomingApprovedHours
          ?.futApprovedOtherHours?.[subType] ?? 0
        ).toFixed(2),
      hoursInThisRequest:
        getOtherHours(subType)
    })
  })
  /*
   * Flexible Personal Days
   */
  rows.push({
    timeOffType:
      getRequestDetailsTypeLabel('D'),
    currentBalance:
      Number(balance?.fpd?.current ?? 0)
      .toFixed(2),
    availableBalance:
      Number(balance?.fpd?.available ?? 0)
      .toFixed(2),
    futureHoursApproved:
      Number(
        upcomingApprovedHours?.futApprovedFlexHours ?? 0
      ).toFixed(2),
    hoursInThisRequest:
      getHours('D')
  })
  /*
   * Total Row Calculation
   */
  const totals = rows.reduce(
    (acc,row)=>{
      const current =
        Number(row.currentBalance)
      const available =
        Number(row.availableBalance)
      const future =
        Number(row.futureHoursApproved)
      const request =
        Number(row.hoursInThisRequest)
      if(!Number.isNaN(current))
        acc.current += current
      if(!Number.isNaN(available))
        acc.available += available
      if(!Number.isNaN(future))
        acc.future += future
      if(!Number.isNaN(request))
        acc.request += request
      return acc
    },
    {
      current:0,
      available:0,
      future:0,
      request:0
    }
  )
  rows.push({
    timeOffType:'Total',
    currentBalance:
      totals.current.toFixed(2),
    availableBalance:
      totals.available.toFixed(2),
    futureHoursApproved:
      totals.future.toFixed(2),
    hoursInThisRequest:
      totals.request.toFixed(2),
    _totalRow:true,
    _rowStyle:{
      backgroundColor:'#D3D6DC',
      border:'1px solid #878F9B'
    }
  })

  // Add thousands separators for display only, once every value has already
  // been read as a plain number above (the totals reduce over row values via
  // Number(row.currentBalance) etc. — comma-formatted strings would parse as
  // NaN there, silently dropping that row from the sum).
  return rows.map((row) => ({
    ...row,
    currentBalance: formatBalanceCell(row.currentBalance),
    availableBalance: formatBalanceCell(row.availableBalance),
    futureHoursApproved: formatBalanceCell(row.futureHoursApproved),
    hoursInThisRequest: formatBalanceCell(row.hoursInThisRequest),
  }))
},[
  requestedDates,
  balance,
  upcomingApprovedHours,
  data
])
  const requestDetailsColumns = [
    { key: 'timeOffType', label: 'Time Off Request Type', className: '' },
    { key: 'currentBalance', label: 'Current Balance', className: '' },
    { key: 'availableBalance', label: 'Available Balance', className: '' },
    { key: 'futureHoursApproved', label: 'Future Hours Approved', className: '' },
    { key: 'hoursInThisRequest', label: 'Hours in This Request', className: '' },
  ]
  return (
    <>

      {/* Current Status Section */}
      {/* <div className="w-full mt-6">
        <CurrentStatus
          statuses={data?.statuses}
          partialRefId={data?.partialRefId}
      />
      </div> */}

      {/* Current Request Section */}
      <div className="w-full mt-6 pb-6!">
        <h2 className="h2">Current Request</h2>
        <div className="mt-4">
          <Table
            columns={currentRequestColumns}
            rows={currentRequestRows}
            isSorting={false}
            showPagination={false}
          />
        </div>
      </div>

      {/* Request History Section */}
      <RequestHistory
        auditHistory={data?.auditHistory}
        partialRefId={data?.partialRefId}
        manualReqRcvdDate={data?.manualReqRcvdDate}
        isVacationBid={requestedDates?.some((date) => date.requestType === 'A') ?? false}
        ownerNuid={data?.empNUID}
      />

      {/* Request Details Section */}
      <div className="w-full mt-6">
        <h2 className="h2">Request Details</h2>
        <div className="mt-4">
          <Table
            columns={requestDetailsColumns}
            rows={requestDetailsRows}
            isSorting={false}
            showPagination={false}
          />
        </div>
      </div>
            {balance?.birthday?.eligible && (
            <div className="mt-4! flex gap-68!">
              <span className="p3 -medium">Birthday</span>
              <span className="p3 -medium">{balance?.birthday?.status}</span>
            </div>
          )}
    </>
  )
}

export default DetailsTable
